import { featureFlags } from '@config/featureFlags';
import type { EventRead } from '@models/api';
import { getEventPhase, isWithinStartingSoonBand, type LiveEventPhase } from '@utils/eventLiveness';
import { LiveBadge } from './LiveBadge';
import { LiveStateChip } from './LiveStateChip';

// The teal "Starting soon" copy lives with the chip that renders it
// (LiveStateChip); re-exported here so `@components/live/EventPhaseBadge` stays
// the import path for the Home rail's pill copy (liveHomeCopy composes the same
// word). Keep this path exporting it — a stacked consumer imports it from here.
// eslint-disable-next-line react-refresh/only-export-components -- re-export of a copy constant for the stacked liveHomeCopy import path; not an HMR boundary.
export { STARTING_SOON_LABEL } from './LiveStateChip';

/**
 * Where a phase badge sits. Each variant is a physical placement, not a boolean
 * mode (composition over flags) — chrome AND which tiers show are fixed per
 * placement:
 *   - `card-corner` floats in a cover's corner (elevation); shows live / soon /
 *     the teal "Starting soon" band. Ended is silent — the card body owns recap.
 *   - `meta` sits inline beside a date; shows soon / teal band / ended. Live is
 *     silent (the hero LiveBadge owns the red treatment, L9).
 *   - `meta-live` is `meta` for a hero-less surface (the compact header): it
 *     ALSO surfaces the inline LiveBadge so liveness still shows.
 *   - `card-chip` is the space-rail card's on-image "when" badge (`SlotCardChip`,
 *     W2.7): the UNIVERSAL cue that ALWAYS renders — live / countdown / teal band /
 *     and, uniquely, a compact `date` beyond the band (every other placement stays
 *     silent there and lets the surface show its own date copy). `SlotCardChip`
 *     maps the resolved kind to the card's own grey/red visual treatment.
 */
export type EventPhaseBadgeVariant = 'card-corner' | 'meta' | 'meta-live' | 'card-chip';

/**
 * The placements `EventPhaseBadge` itself renders — the full ladder MINUS
 * `card-chip`. That variant's beyond-band tier resolves to kind `'date'`, a
 * SlotCardChip-only grey-pill/compact-date treatment this component has no
 * render branch for (SlotCardChip consumes that kind through
 * `resolveEventPhaseBadgeKind` directly). Excluding it keeps the component and
 * `hasVisibleEventPhaseBadge` in lockstep: for every variant the component
 * accepts, the predicate is true exactly when a badge renders, so a host that
 * mounts a positioned wrapper only when the predicate passes never ends up with
 * an empty one.
 */
export type EventPhaseBadgeRenderVariant = Exclude<EventPhaseBadgeVariant, 'card-chip'>;

export interface EventPhaseBadgeProps {
  /** The event's window — the badge derives its phase from these plus `now`. */
  event: Pick<EventRead, 'start_date' | 'end_date'>;
  /**
   * The host's clock (from `useEventPhase` / `useNow`). Injected, never
   * internal: this component mounts NO timer, so it never ticks a feed list
   * (spec §3). Hosts pass the same clock they already derive phase against.
   */
  now: Date;
  /** Placement — `card-chip` is SlotCardChip's cue, never this component's. */
  variant: EventPhaseBadgeRenderVariant;
  /**
   * Pre-derived phase from a host that already computed it (e.g.
   * SimpleEventCard's `useEventPhase`), threaded through to the shared resolver
   * so it does NOT recompute `getEventPhase` on every tick. Optional — omitted,
   * the resolver derives it from `event` + `now`, so standalone callers pass
   * only event + now.
   */
  phase?: LiveEventPhase;
}

/**
 * The badge the ladder resolves to for a phase+placement, or null when silent.
 * `date` is the `card-chip`-only tier: the compact event date the space-rail card
 * shows beyond the "Starting soon" band (every other placement stays silent there).
 */
export type EventPhaseBadgeKind = 'live' | 'soon' | 'starting-soon' | 'ended' | 'date' | null;

/**
 * The ONE phase→badge decision, shared by the component, `SlotCardChip`, AND
 * `hasVisibleEventPhaseBadge` so no surface's "which cue?" (or a host's "should I
 * mount the slot?") can drift from what actually renders. Pure: the phase, the
 * `startingSoonLeadMs` band, and each placement's silences. `null` = nothing
 * renders. The `liveEvents` kill switch is applied by every caller, not here.
 *
 * `phase` is injectable: a host that already derived the event's phase (e.g.
 * `pickSlotCells` for the space rail) threads it in so the resolver does NOT
 * recompute `getEventPhase` per tick. Omitted, it derives the phase itself, so
 * standalone callers (and the badge tests) need pass only event + now.
 */
// eslint-disable-next-line react-refresh/only-export-components -- the badge ladder's shared kind resolver, co-located so SlotCardChip and the visibility predicate can't drift from the component; not an HMR boundary.
export function resolveEventPhaseBadgeKind(
  event: Pick<EventRead, 'start_date' | 'end_date'>,
  now: Date,
  variant: EventPhaseBadgeVariant,
  phase: LiveEventPhase = getEventPhase(event, now),
): EventPhaseBadgeKind {
  if (phase === 'live') {
    // The meta row stays silent while live — the hero LiveBadge owns L9's red
    // treatment; a hero-less caller opts in via `meta-live`.
    return variant === 'meta' ? null : 'live';
  }
  if (phase === 'soon') {
    return 'soon';
  }
  if (phase === 'upcoming') {
    // Teal "Starting soon" only inside the rolling band. Beyond it the card chip
    // (the universal when-cue) shows the compact date, while every other placement
    // yields nothing and the surface shows its own date copy — the band is a badge
    // concern, not a phase boundary.
    if (isWithinStartingSoonBand(event, now)) {
      return 'starting-soon';
    }
    return variant === 'card-chip' ? 'date' : null;
  }
  if (phase === 'ended-recap') {
    // Ended shows only on the meta rows; the card corner and the card chip are
    // silent — the card body's recap copy / status line owns it.
    return variant === 'card-corner' || variant === 'card-chip' ? null : 'ended';
  }
  return null;
}

/**
 * Whether `EventPhaseBadge` renders a visible badge for this event + placement —
 * the predicate a host consults before mounting the badge's wrapper, so a
 * beyond-band `upcoming` (or a silent card-corner ended) leaves NO empty
 * positioned div. Derived from the same ladder the component renders and gated
 * by the same `liveEvents` kill switch, so it cannot disagree with what mounts.
 *
 * `phase` is injectable exactly as on `resolveEventPhaseBadgeKind`: a host that
 * already derived the phase threads it so the predicate does not recompute
 * `getEventPhase`. Omitted, it derives the phase itself.
 */
// eslint-disable-next-line react-refresh/only-export-components -- the badge ladder's shared visibility predicate, co-located so it can't drift from the component; not an HMR boundary.
export function hasVisibleEventPhaseBadge(
  event: Pick<EventRead, 'start_date' | 'end_date'>,
  now: Date,
  variant: EventPhaseBadgeVariant = 'card-corner',
  phase?: LiveEventPhase,
): boolean {
  if (!featureFlags.liveEvents) {
    return false;
  }
  return resolveEventPhaseBadgeKind(event, now, variant, phase) !== null;
}

/**
 * The ONE place that maps a live phase to its badge (Live Layer v2). Every
 * surface that shows a phase cue renders this instead of assembling its own
 * live/soon/ended ladder, so the mapping — and the teal "Starting soon" band —
 * can never drift between call sites. `LiveBadge` (the red live pill) and
 * `LiveStateChip` (soon countdown / teal "Starting soon" / grey ended) are the
 * shared visual primitives this composes.
 *
 * Flag-gated: the `liveEvents` kill switch blanks every badge, so a host can
 * render this unconditionally and the switch still silences it. A host that must
 * not mount an empty wrapper for a silent placement gates on
 * `hasVisibleEventPhaseBadge` first (both read the same ladder).
 */
export function EventPhaseBadge({ event, now, variant, phase }: EventPhaseBadgeProps) {
  if (!featureFlags.liveEvents) {
    return null;
  }

  const chrome = variant === 'card-corner' ? 'on-image' : 'inline';
  switch (resolveEventPhaseBadgeKind(event, now, variant, phase)) {
    case 'live':
      return <LiveBadge variant={chrome} />;
    case 'soon':
      return <LiveStateChip phase="soon" startDate={event.start_date} now={now} variant={chrome} />;
    case 'starting-soon':
      return <LiveStateChip phase="starting-soon" variant={chrome} />;
    case 'ended':
      return <LiveStateChip phase="ended" variant={chrome} />;
    default:
      return null;
  }
}
